<template>
  <section class="reviews-section">
    <div class="reviews-section__container">
      <div class="section-header">
        <h2 class="section-header__title">LATEST REVIEWS</h2>
        <div class="section-header__line"></div>
        <a href="/reviews/" class="section-header__link">SEE ALL</a>
      </div>

      <div v-if="reviewArticles.length > 0" class="reviews-grid">
        <article
          v-for="article in reviewArticles"
          :key="article.slug"
          class="review-card"
        >
          <a :href="`/${article.category}/${article.slug}/`" class="review-card__link">
            <div v-if="article.featuredImage || article.image" class="review-card__image">
              <img :src="article.featuredImage || article.image" :alt="article.title" />
              <div v-if="article.score" class="review-card__score">
                {{ article.score }}
              </div>
            </div>
            <div class="review-card__content">
              <span class="review-card__tag">{{ formatReviewLabel(article) }}</span>
              <h3 class="review-card__title">{{ article.title }}</h3>
              <p class="review-card__excerpt">{{ article.excerpt }}</p>
            </div>
          </a>
        </article>
      </div>
      <div v-else class="empty-state">
        <p>NO REVIEWS DETECTED.</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ReviewsSection',
  computed: {
    reviewArticles() {
      const articles = this.$store.getters['articles/getArticlesByCategory']('reviews')
      if (!articles || articles.length === 0) return []
      const reviewLaneArticles = articles.filter(this.isReviewLaneArticle)
      return (reviewLaneArticles.length > 0 ? reviewLaneArticles : articles).slice(0, 3)
    },
  },
  methods: {
    isReviewLaneArticle(article) {
      const subcategory = (article.subcategory || '').toLowerCase()
      const title = (article.title || '').toLowerCase()
      const slug = (article.slug || '').toLowerCase()
      if (slug.includes('tier-list') || title.includes('tier list')) return false
      return subcategory.includes('review') || title.includes('review') || slug.includes('review')
    },

    formatReviewLabel(article) {
      const rawLabel = article.subcategory || 'review'
      return rawLabel
        .split('-')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
.reviews-section {
  padding: rem(48) 0;
  background: $bg-secondary;
  border-top: 2px solid $border-color;
  border-bottom: 2px solid $border-color;
}

.reviews-section__container {
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 rem(24);
}

.section-header {
  display: flex;
  align-items: center;
  gap: rem(24);
  margin-bottom: rem(48);

  &__title {
    font-size: rem(32);
    color: $text-primary;
    font-weight: 900;
    font-family: $font-family-heading;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0;
  }

  &__line {
    flex: 1;
    height: 2px;
    background-color: $border-color;
  }

  &__link {
    color: $primary-color;
    font-family: $font-family-heading;
    font-size: rem(16);
    font-weight: 900;
    text-transform: uppercase;
    text-decoration: none;
    letter-spacing: 1px;
    transition: color $transition-speed;

    &:hover {
      color: $text-primary;
    }
  }
}

.reviews-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: rem(24);

  @include pc {
    grid-template-columns: repeat(3, 1fr);
    gap: rem(32);
  }
}

.review-card {
  background: $bg-primary;
  border: 1px solid $border-color;
  display: flex;
  flex-direction: column;
  position: relative;
  @include brutal-hover;

  &__link {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none;
    color: inherit;
  }

  &__image {
    position: relative;
    width: 100%;
    height: rem(250);
    overflow: hidden;
    border-bottom: 1px solid $border-color;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }
  }

  &:hover &__image img {
    transform: scale(1.05);
  }

  &__score {
    position: absolute;
    bottom: rem(-24);
    right: rem(24);
    background: $primary-color;
    color: #fff;
    width: rem(56);
    height: rem(56);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: rem(24);
    font-weight: 900;
    font-family: $font-family-heading;
    border: 4px solid $bg-primary;
    z-index: 2;
  }

  &__content {
    padding: rem(32) rem(24) rem(24);
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__tag {
    color: $primary-color;
    font-size: rem(12);
    font-weight: 900;
    font-family: $font-family-heading;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: rem(12);
  }

  &__title {
    font-size: rem(24);
    color: $text-primary;
    margin: 0 0 rem(12) 0;
    line-height: 1.1;
    font-family: $font-family-heading;
    font-weight: 900;
    text-transform: uppercase;
  }

  &__excerpt {
    font-size: rem(14);
    color: $text-secondary;
    line-height: 1.6;
    margin: 0;
    @include text-ellipsis(3);
  }
}

.empty-state {
  text-align: center;
  padding: rem(64);
  font-family: $font-family-heading;
  font-weight: 900;
  font-size: rem(24);
  color: $text-muted;
  border: 2px dashed $border-color;
  text-transform: uppercase;
}
</style>
